import { Text, Circle } from "react-konva";
import { LockerRender } from "./LockerRender";
import { Ruler } from "./Ruler";
import { styleSchema } from "./style";
import { HPOS, VPOS } from "./coord";
import range from "lodash/range";
import { getMaxRows, getRowsByColumn } from "./tools";
import { MyRect } from "./MyRect";
import { KonvaMuiIcon } from "./KonvaMuiIcon";
import { OnlineStatusField } from "../tower/towers";

export const getLockersFrame = (towerFrame) => {
  const padding = {
    top: styleSchema.towerPaddingPerc.top * towerFrame.size?.height,
    bottom: styleSchema.towerPaddingPerc.bottom * towerFrame.size?.height,
    left: styleSchema.towerPaddingPerc.h * towerFrame.size?.width,
    right: styleSchema.towerPaddingPerc.h * towerFrame.size?.width,
  };

  return {
    pos: {
      x: towerFrame.pos?.x + padding.left,
      y: towerFrame.pos?.y + padding.top,
    },
    size: {
      width: towerFrame.size?.width - padding.left - padding.right,
      height: towerFrame.size?.height - padding.top - padding.bottom,
    },
  };
};

const getBottomFrame = (towerFrame, lockersFrame) => {
  const lockersFrameTopPadding = lockersFrame.pos?.y - towerFrame.pos?.y;

  return {
    pos: {
      x: lockersFrame.pos?.x,
      y: lockersFrame.pos?.y + lockersFrame.size?.height,
    },
    size: {
      width: lockersFrame.size?.width,
      height:
        towerFrame.size?.height -
        lockersFrame.size?.height -
        lockersFrameTopPadding -
        towerFrame.padding,
    },
  };
};

export const TowerFace = ({
  x,
  y,
  width,
  height,
  numColumns,
  lockers,
  types,
  lockerNameGenerator,
  options,
  padding = 5,
  tower,
  ...rest
}) => {
  if (!types) return null;

  const towerFrame = {
    pos: {
      x,
      y,
    },
    size: {
      width,
      height,
    },
    padding,
  };
  const lockersFrame = getLockersFrame(towerFrame);
  const bottomFrame = getBottomFrame(towerFrame, lockersFrame);

  const maxCols = numColumns;
  const maxRows = getMaxRows(lockers, types);

  const baseLockerSize = {
    width: lockersFrame.size.width / maxCols,
    height: lockersFrame.size.height / maxRows,
  };

  let rowsByColumn = Object.assign(
    {},
    ...range(1, parseInt(numColumns) + 1).map((k) => ({ [k]: 0 }))
  );
  rowsByColumn = Object.assign(
    {},
    rowsByColumn,
    getRowsByColumn(lockers, types)
  );
  const freeSpacePlaceholders = [];
  for (const col in rowsByColumn) {
    const free = maxRows - rowsByColumn[col];
    if (free) {
      freeSpacePlaceholders.push({
        colPosition: parseInt(col),
        rowPosition: rowsByColumn[col] + 1,
        offsetPosition: 0,
        width: 1,
        height: free,
        lockerType: 1,
        ...styleSchema.locker.freeSpace,
        type: 1,
      });
    }
  }

  const towerFaceStyle = {
    backgroundImage: {
      src: options.bg_src,
      fitMode: options.bg_fit,
      opacity: options.bg_opacity ? options.bg_opacity / 100 : null,
    },
  };

  const lockersToRender = lockers.concat(freeSpacePlaceholders);

  let renderedLockers = [];
  lockersToRender.forEach((spec, index) => {
    renderedLockers = renderedLockers.concat(
      <LockerRender
        key={index}
        {...{
          x: lockersFrame.pos.x,
          y: lockersFrame.pos.y,
          spec,
          types,
          baseLockerSize,
          index,
          lockerNameGenerator,
          options,
          ...rest,
        }}
      />
    );
  });

  return (
    <>
      <MyRect
        className={"TowerFrame"}
        coords={towerFrame}
        {...{ ...styleSchema.towerFace, ...towerFaceStyle }}
      >
        <MyRect
          fill="rgba(0,0,0,1)"
          width={12}
          height={9}
          vpos={VPOS.TOP}
          hpos={HPOS.LEFT}
          offset={{ x: 8, y: -2 }}
        />
        <Circle
          fill="rgba(0,0,0,1)"
          radius={5}
          vpos={VPOS.TOP}
          hpos={HPOS.MIDDLE}
          offset={{ x: 0, y: -2 }}
        />
        <MyRect
          fill="rgba(250,250,250,1)"
          width={20}
          height={9}
          vpos={VPOS.TOP}
          hpos={HPOS.RIGHT}
          offset={{ x: 0, y: -2 }}
        />
        {options.proportional && (
          <Text
            label="1:1"
            vpos={VPOS.TOP}
            hpos={HPOS.RIGHT}
            offset={{ x: 0, y: -3 }}
          />
        )}
      </MyRect>

      {renderedLockers}

      {["tower", "all"].includes(options.ruler) && (
        <Ruler
          frame={lockersFrame}
          numColumns={numColumns}
          numRows={maxRows}
          options={options}
          inside={false}
        />
      )}
      <MyRect coords={bottomFrame}>
        {options.online && tower && (
          <KonvaMuiIcon
            fill={tower.isOnline ? "green" : "red"}
            MuiIcon={<OnlineStatusField tower={tower} />}
            size={{ width: 24, height: 24 }}
            vpos={VPOS.CENTER}
            hpos={HPOS.MIDDLE}
          />
        )}
      </MyRect>
    </>
  );
};
